import { useQuery } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { configService } from '@/services/config.service'
import logoImg from '@/assets/logo-icon.png'
import logoSvg from '@/assets/logo.svg'

export function FlyerPage() {
  const { data: config } = useQuery({
    queryKey: ['config', 'compensation'],
    queryFn: () => configService.getCompensationConfig(),
    staleTime: 60_000,
  })

  const hourly = config?.hourlyRate ?? '–'
  const bonus = config?.bonusPerRegistration ?? '–'

  return (
    <>
      <style>{`@media print { @page { margin: 0; } body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <div className="w-[210mm] mx-auto bg-white px-12 py-10 print:px-10 print:py-8 print:m-0 print:shadow-none shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <img src={logoSvg} alt="U Like It" className="h-11" />
          <span className="text-sm text-gray-400 print:hidden">Drucken: Strg+P</span>
        </div>

        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#024ab0] mb-2">
            Verdiene Geld in deiner Stadt
          </h1>
          <p className="text-xl text-[#ff5801] font-semibold">
            Werde U-Like-It Scout
          </p>
        </div>

        {/* Value Prop */}
        <div className="bg-gradient-to-r from-[#107bd4]/10 to-[#024ab0]/10 rounded-xl p-5 mb-8 border border-[#107bd4]/20">
          <p className="text-lg text-center text-gray-800">
            Besuche lokale Geschäfte, stelle unsere Plattform vor und verdiene{' '}
            <span className="font-bold text-[#024ab0]">{hourly} €/Stunde</span> +{' '}
            <span className="font-bold text-[#ff5801]">{bonus} € Bonus</span> pro Händler-Anmeldung.
          </p>
        </div>

        {/* Steps */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#024ab0] mb-5 text-center">
            In 3 Schritten loslegen
          </h2>
          <div className="grid grid-cols-3 gap-5">
            <div className="text-center rounded-lg p-4 bg-[#107bd4] text-white">
              <div className="w-12 h-12 bg-white text-[#107bd4] rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                1
              </div>
              <h3 className="font-semibold mb-1">Anmelden</h3>
              <p className="text-sm text-white/80">Name, E-Mail, Handy — dauert 2 Minuten.</p>
            </div>
            <div className="text-center rounded-lg p-4 bg-[#107bd4] text-white">
              <div className="w-12 h-12 bg-white text-[#107bd4] rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                2
              </div>
              <h3 className="font-semibold mb-1">Pitch lernen</h3>
              <p className="text-sm text-white/80">Script lesen, Video aufnehmen oder Interview buchen.</p>
            </div>
            <div className="text-center rounded-lg p-4 bg-[#107bd4] text-white">
              <div className="w-12 h-12 bg-white text-[#107bd4] rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                3
              </div>
              <h3 className="font-semibold mb-1">Loslegen</h3>
              <p className="text-sm text-white/80">Geschäfte auf der Karte reservieren & besuchen.</p>
            </div>
          </div>
        </div>

        {/* What you earn / need */}
        <div className="grid grid-cols-2 gap-5 mb-8">
          <div className="border border-[#107bd4]/30 rounded-lg p-4 bg-[#107bd4]/5">
            <h3 className="font-bold text-[#024ab0] mb-2">Was du verdienst</h3>
            <ul className="space-y-1.5 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#ff5801] font-bold">✓</span>
                {hourly} €/Stunde Arbeitszeit
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#ff5801] font-bold">✓</span>
                {bonus} € Bonus pro Händler-Anmeldung
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#ff5801] font-bold">✓</span>
                Monatliche Auszahlung
              </li>
            </ul>
            <p className="text-xs text-gray-500 mt-2">
              Bankverbindung unter „Profil" hinterlegen für die Auszahlung.
            </p>
          </div>
          <div className="border border-[#107bd4]/30 rounded-lg p-4 bg-[#107bd4]/5">
            <h3 className="font-bold text-[#024ab0] mb-2">Was du brauchst</h3>
            <ul className="space-y-1.5 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#107bd4] font-bold">→</span>
                Smartphone
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#107bd4] font-bold">→</span>
                Freundliches Auftreten
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#107bd4] font-bold">→</span>
                2–3 Stunden pro Woche (oder mehr)
              </li>
            </ul>
          </div>
        </div>

        {/* QR Code CTA */}
        <div className="border-2 border-[#ff5801] rounded-xl p-6 text-center bg-gradient-to-b from-[#ff5801]/5 to-white">
          <p className="text-lg font-bold text-[#024ab0] mb-3">
            Jetzt scannen & mehr erfahren:
          </p>
          <div className="flex justify-center mb-2">
            <QRCodeSVG
              value="https://scout.u-like-it.de"
              size={150}
              level="H"
              includeMargin={false}
              fgColor="#024ab0"
              imageSettings={{
                src: logoImg,
                x: undefined,
                y: undefined,
                height: 30,
                width: 30,
                excavate: true,
              }}
            />
          </div>
          <p className="text-sm text-gray-500">scout.u-like-it.de</p>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-3 border-t border-gray-200 text-center text-xs text-gray-400">
          FSM GmbH · support@u-like-it.de
        </div>
      </div>
    </>
  )
}
